import { Injectable, Inject, DOCUMENT } from '@angular/core';


export interface ICookieOptions {
    expires?: number | Date;
    path: string;
    domain: string;
    secure: boolean;
    sameSite: 'Lax' | 'None' | 'Strict';
}

@Injectable({
    providedIn: 'root'
})
export class CookieStorageService {
    constructor(@Inject(DOCUMENT) private document: Document) {}

    /**
     * @param name Cookie name
     * @returns boolean - whether cookie with specified name exists
     */
    check(name: string): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        name = encodeURIComponent(name);

        const regExp: RegExp = this.getCookieRegExp(name);
        const exists: boolean = regExp.test(this.document.cookie);

        return exists;
    }

    /**
     * @param name Cookie name
     * @returns property value
     */
    get(name: string): string {
        if (typeof window === 'undefined') {
            return '';
        }
        if (this.check(name)) {
            name = encodeURIComponent(name);

            const regExp: RegExp = this.getCookieRegExp(name);
            const result: RegExpExecArray | null = regExp.exec(this.document.cookie);

            return result ? this.safeDecodeURIComponent(result[1]) : '';
        } else {
            return '';
        }
    }

    /**
     * @returns all the cookies in json
     */
    getAll(): { [key: string]: string } {
        const cookies: { [key: string]: string } = {};
        const document: any = this.document;

        if (document.cookie && document.cookie !== '') {
            document.cookie.split(';').forEach(currentCookie => {
                const [cookieName, cookieValue] = currentCookie.split('=');
                cookies[this.safeDecodeURIComponent(cookieName.replace(/^ /, ''))] = this.safeDecodeURIComponent(cookieValue);
            });
        }

        return cookies;
    }

    /**
     * @param name     Cookie name
     * @param value    Cookie value
     * @param expires  Number of days until the cookies expires or an actual `Date`
     * @param path     Cookie path
     * @param domain   Cookie domain
     * @param secure   Secure flag
     * @param sameSite OWASP samesite token `Lax`, `None`, or `Strict`. Defaults to `Lax`
     */
    set(name: string, value: string, requestOptions?: Partial<ICookieOptions>): void {
        if (typeof window === 'undefined') {
            return;
        }
        let cookieString: string = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';

        const options: ICookieOptions = Object.assign(
            {
                path: '/',
                domain: location.hostname,
                secure: true,
                sameSite: 'Lax'
            },
            requestOptions
        );

        if (options.expires) {
            if (typeof options.expires === 'number') {
                const dateExpires: Date = new Date(new Date().getTime() + options.expires * 1000 * 60 * 60 * 24);

                cookieString += 'expires=' + dateExpires.toUTCString() + ';';
            } else {
                cookieString += 'expires=' + options.expires.toUTCString() + ';';
            }
        }

        cookieString += 'path=' + options.path + ';';
        cookieString += 'domain=' + options.domain + ';';

        if (options.secure === false && options.sameSite === 'None') {
            options.secure = true;
            console.warn(
                `[ngx-cookie-service] Cookie ${name} was forced with secure flag because sameSite=None.` +
                    `More details : https://github.com/stevermeister/ngx-cookie-service/issues/86#issuecomment-597720130`
            );
        }

        if (options.secure) {
            cookieString += 'secure;';
        }

        this.document.cookie = cookieString;
    }

    /**
     * @param name   Cookie name
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    remove(name: string, requestOptions?: Partial<ICookieOptions>): void {
        this.set(name, '', { ...requestOptions, expires: new Date('Thu, 01 Jan 1970 00:00:01 GMT') });
    }

    /**
     * @param path   Cookie path
     * @param domain Cookie domain
     */
    deleteAll(path?: string, requestOptions?: Partial<ICookieOptions>): void {
        const cookies: any = this.getAll();

        for (const cookieName in cookies) {
            if (cookies.hasOwnProperty(cookieName)) {
                this.remove(cookieName, requestOptions);
            }
        }
    }

    /**
     * @param name Cookie name
     * @returns property RegExp
     */
    private getCookieRegExp(name: string): RegExp {
        const escapedName: string = name.replace(/([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/gi, '\\$1');

        return new RegExp('(?:^' + escapedName + '|;\\s*' + escapedName + ')=(.*?)(?:;|$)', 'g');
    }

    private safeDecodeURIComponent(encodedURIComponent: string): string {
        try {
            return decodeURIComponent(encodedURIComponent);
        } catch {
            // probably it is not uri encoded. return as is
            return encodedURIComponent;
        }
    }
}
